import Image from 'next/image'
import { Trans, useTranslation } from 'next-i18next'
import styled from 'styled-components'

import { breakpoints, colors, fontSizes } from '@/styles'
import Head from '@/components/head'

import heroBanner from './belmontproperties-banner.jpg'

const BannerWrapper = styled.div`
  text-align: center;
  margin-bottom: 30px;
  height: 512px;
  overflow: hidden;
  position: relative;

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    height: auto;
  }
`

const Vignette = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background: rgba(20, 20, 20, 0.35);
`

const Container = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  height: 100%;
  width: 100%;
  padding: 0 20px;
  display: flex;
  justify-content: center;
  align-items: center;

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    position: static;
    transform: translate(0, 0);
    padding-left: 25px;
    padding-right: 25px;
    padding-top: 60px;
    padding-bottom: 60px;
  }
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1;
`

const CompanyName = styled.h1`
  color: ${colors.white};
  font-size: 48px;
  line-height: 65px;
  letter-spacing: 0.62px;
  margin: 0 0 45px;
  font-weight: 400;
  text-shadow: 1px 2px 3px rgba(0, 0, 0, 0.4);

  @media screen and (max-width: 930px) {
    font-size: 40px;
    line-height: 57px;
    letter-spacing: 0.55px;
  }

  @media screen and (max-width: 670px) {
    font-size: 36px;
  }

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    font-size: 22px;
    line-height: 30px;
    letter-spacing: 0.29px;
  }
`

const HorizontalRule = styled.div`
  width: 380px;
  margin-bottom: 40px;
  border-bottom: 2px solid rgba(255, 255, 255, 0.6);

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    width: 220px;
  }
`

const Description = styled.p`
  color: ${colors.white};
  font-size: 17px;
  line-height: 24px;
  font-weight: 400;
  letter-spacing: 0.23px;
  margin-bottom: 50px;
  text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.5);
  max-width: 890px;

  small {
    font-size: ${fontSizes.small};
    line-height: 22px;
  }
`

const SectionTitle = styled.h3`
  color: ${colors.regular};
  font-size: ${fontSizes.medium};
  font-weight: bold;
  letter-spacing: -0.49px;
  line-height: 22px;
  text-align: center;
  margin-bottom: 30px;

  @media screen and (max-width: 700px) {
    display: none;
  }
`

const ContactInfo = styled.p`
  color: ${colors.white};
  font-size: 22px;
  line-height: 30px;
  font-weight: 400;
  letter-spacing: 0.23px;
  text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.5);

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    font-size: 18px;
    line-height: 24px;
  }
`

const PlainLink = styled.a`
  color: inherit;
  text-decoration: none;
  cursor: pointer;
`

const SmallLink = styled.a`
  font-size: 17px;
  line-height: 24px;
  font-weight: 400;
  letter-spacing: 0.23px;
  color: inherit;
  text-decoration: none;
  cursor: pointer;

  @media only screen and (max-width: ${breakpoints.tabletPortrait}) {
    font-size: ${fontSizes.small};
  }
`

const BelmontPropertiesProfile = ({ metadata }) => {
  const { t } = useTranslation('belmontproperties')

  const profile = {
    banner: heroBanner.src,
    title: t('belmontproperties:Belmont Properties'),
    pageDescription: t('belmontproperties:description'),
    url: '/company/belmontproperties'
  }

  return (
    <>
      <Head
        title={`${profile.title} | liv.rent`}
        description={profile.pageDescription}
        // meta={{
        //   image: `${BASE_URL}/images/w-brothers-banner.jpg`,
        //   imageAlt: t('belmontproperties:Belmont Properties')
        // }}
        path={profile.url}
        pageData={metadata}
        hideCustomerSupport
      />
      <BannerWrapper>
        <Image
          fill
          priority
          src={profile.banner}
          alt="Belmont Properties"
          sizes="(max-width: 768px) 768px, (max-width: 1200px) 1200px"
        />
        <Container>
          <Vignette />
          <ContentContainer>
            <CompanyName>{profile.title}</CompanyName>
            <HorizontalRule />
            <Description>
              <Trans i18nKey="belmontproperties:description">
                Belmont is a property management company providing rentals that
                are comfortable, affordable, and close to parks, local
                amenities, and good schools for over 45 years.
                <br />
                Welcome to the neighbourhood! Our rental units in various
                locations offer different rent rates, giving residents the
                option to downsize or grow with their lifestyle in familiar
                neighborhoods of their choosing
              </Trans>
            </Description>
            <ContactInfo>
              <PlainLink href="mailto:info@belmontproperties.ca">
                info@belmontproperties.ca
              </PlainLink>
              {' | '}
              <PlainLink href="tel:1-604-736-2841">1-604-736-2841</PlainLink>
              <br />
              <SmallLink
                href="https://www.belmontproperties.ca/"
                target="_blank"
              >
                www.belmontproperties.ca
              </SmallLink>
            </ContactInfo>
          </ContentContainer>
        </Container>
      </BannerWrapper>
      <SectionTitle>{t('belmontproperties:Our Portfolio')}</SectionTitle>
    </>
  )
}

export default BelmontPropertiesProfile
